import { useState, useEffect, useCallback } from 'react';

export const useNotifications = () => {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if notifications are supported
    if ('Notification' in window) {
      setIsSupported(true);
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) return false;

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported]);

  const showNotification = useCallback((title, options = {}) => {
    if (!isSupported || permission !== 'granted') {
      console.log('Notifications not permitted');
      return null;
    }

    try {
      const notification = new Notification(title, {
        badge: '/icons/badge-96x96.png',
        tag: options.tag || 'nerava-notification',
        renotify: true,
        requireInteraction: false,
        silent: false,
        ...options
      });

      // Auto close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) {
          options.onClick();
        }
      };

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }, [isSupported, permission]);

  const notifyNewMessage = useCallback((customerName, message, conversationId, navigate) => {
    showNotification(customerName || 'Customer', {
      body: message?.substring(0, 100) || 'Ada pesan baru',
      tag: `conversation-${conversationId}`,
      onClick: () => {
        if (navigate && conversationId) {
          navigate(`/inbox/${conversationId}`);
        }
      }
    });
  }, [showNotification]);

  return {
    isSupported,
    permission,
    requestPermission,
    showNotification,
    notifyNewMessage
  };
};

export default useNotifications;
